import { createPortal } from "react-dom";

import { useScheduleContext } from "../../../../context/domain/planning/ScheduleContext";
import { toLocalDateKey } from "../../../../utils/datetime/core";
import DayDetails from "../../shared/DayDetails";

/**
 * Schedule adapter for the shared planning DayDetails card.
 *
 * The adapter owns Schedule hover positioning and context lookups, then passes
 * date-specific DayDetails, weather, and derived worker groups to the shared card.
 *
 * @param {Object} props
 * @param {Date} props.day Calendar day shown in the header cell.
 * @param {number} props.top Viewport Y anchor (top edge of the header cell).
 * @param {number} props.left Viewport X anchor (horizontal center of the cell).
 * @param {() => void} [props.onMouseEnter] Keeps the card open when hovered.
 * @param {() => void} [props.onMouseLeave] Closes the card after hover leaves.
 * @returns {import('react').ReactPortal|null}
 */
export default function ScheduleDayDetailsHoverCard({
  day,
  top,
  left,
  onMouseEnter,
  onMouseLeave,
}) {
  const {
    getScheduleDayDetails,
    getScheduleWeather,
    setScheduleDayLocked,
    workScopeAspects,
    workers,
  } = useScheduleContext();
  const dateKey = toLocalDateKey(day);
  const dayDetails = getScheduleDayDetails(dateKey);
  const weather = getScheduleWeather(dateKey);
  const offWorkWorkers = dayDetails?.off_work ?? [];
  const assignedWorkers = getUniqueAssignedWorkersForDay(workScopeAspects, dateKey);
  const unavailableIds = new Set(
    [...assignedWorkers, ...offWorkWorkers].map((worker) => Number(worker.id))
  );
  const availableWorkers = (workers ?? []).filter(
    (worker) => !unavailableIds.has(Number(worker.id))
  );

  if (typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div
      style={{
        top,
        left,
        transform: "translate(-50%, calc(-100% - 4px))",
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className="pointer-events-auto fixed z-50 w-64 font-inter"
    >
      <DayDetails
        type="schedule"
        date={day}
        dayDetails={dayDetails}
        weather={weather}
        assignedWorkers={assignedWorkers}
        offWorkWorkers={offWorkWorkers}
        availableWorkers={availableWorkers}
        dayLocked={Boolean(dayDetails?.locked)}
        onDayLockToggle={() => setScheduleDayLocked(dateKey, !dayDetails?.locked)}
      />
    </div>,
    document.body
  );
}

/**
 * Derive the unique non-skipped Schedule crew for one date from visible aspect rows.
 *
 * @param {Array<Object>} workScopeAspects Schedule aspect rows.
 * @param {string} dateKey Local date key in YYYY-MM-DD format.
 * @returns {Array<Object>}
 */
function getUniqueAssignedWorkersForDay(workScopeAspects, dateKey) {
  const workersById = new Map();
  for (const aspect of workScopeAspects ?? []) {
    if (toLocalDateKey(aspect.date) !== dateKey || aspect.skipped) {
      continue;
    }
    for (const worker of aspect.worker_assignment ?? []) {
      workersById.set(Number(worker.id), worker);
    }
  }
  return Array.from(workersById.values());
}
